import {
  Cursor,
  Eye,
  PencilSimple,
  Phone,
  Plus,
  Star,
  Trash,
} from '@phosphor-icons/react';
import { DotsThreeVertical } from '@phosphor-icons/react/dist/ssr';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { AccentButtonOutline } from '../../../../../../shared/Buttons';
import { VendorBusinessLocation } from '../../../../../../types/dbTypes';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';

function BusinessLocations({
  locations,
  loadingLocations,
  addClass,
}: {
  locations: VendorBusinessLocation[];
  loadingLocations: boolean;
  addClass?: string | undefined;
}) {
  const { screenView } = useScreenView();
  const navigate = useNavigate();
  const [popupVisibleMap, setPopupVisibleMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const togglePopup = (index: string | undefined) => {
    if (!index) return;
    setPopupVisibleMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(index, !newMap.get(index)); // Toggle visibility for the item at index
      return newMap;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      let shouldClose = false;
      popupVisibleMap.forEach((isVisible, key) => {
        if (
          isVisible &&
          popupRefs.current[key] &&
          !popupRefs.current[key]?.contains(event.target as Node)
        ) {
          shouldClose = true;
        }
      });

      if (shouldClose) {
        setPopupVisibleMap(new Map()); // Close all popups if clicked outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupVisibleMap]);
  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className={`flex w-[100%] flex-col items-center justify-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            <div className="flex w-full flex-row items-center justify-between space-x-2 border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Business Locations
              </h2>
              <AccentButtonOutline
                onClick={() => navigate('/dashboard/locations/create')}
                addClass={`py-[4px] rounded-full`}
              >
                <div className="flex flex-row items-center space-x-2 align-middle">
                  <Plus />
                  <p>Add Location</p>
                </div>
              </AccentButtonOutline>
            </div>
            {!loadingLocations ? (
              locations?.length > 0 ? (
                <div className="grid h-full min-h-[200px] w-full gap-4 overflow-y-auto overflow-x-hidden p-4 sm:grid-cols-1 lg:grid-cols-3">
                  {locations.map((loc, idx) => (
                    <div
                      key={idx}
                      className="relative flex w-full flex-col justify-between gap-4 rounded-[4px] border-[1px] border-bordergray p-4"
                    >
                      <div className="line-clamp-2 flex h-[48px] max-h-[48px] min-h-[48px] flex-row items-center justify-between overflow-hidden text-ellipsis align-middle">
                        <h2 className="max-w-[90%] text-[16px] font-[600] text-primary">
                          {loc?.address_st_number +
                            ' ' +
                            loc?.address_st_name +
                            (loc?.address_unit ? ' ' + loc?.address_unit : '') +
                            ', ' +
                            loc?.address_city +
                            ', ' +
                            loc?.address_state +
                            ' ' +
                            loc?.address_zip}
                        </h2>
                        <button
                          onClick={(e: any) => {
                            e.stopPropagation();
                            togglePopup(loc?.location_id ?? '');
                          }}
                          className=""
                        >
                          <DotsThreeVertical size={24} weight="bold" />
                          {popupVisibleMap.get(loc?.location_id ?? '') && (
                            <div
                              ref={(el) => {
                                popupRefs.current[loc?.location_id ?? ''] = el;
                              }}
                              className="absolute right-1 top-[3.5rem] z-40 w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                            >
                              <div>
                                <button
                                  onClick={(e: any) => {
                                    e.stopPropagation();
                                    togglePopup(loc?.location_id ?? '');
                                    navigate(
                                      '/dashboard/locations/edit/' +
                                        loc?.location_id
                                    );
                                  }}
                                  className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                >
                                  <PencilSimple size={20} />
                                  <p>Edit</p>
                                </button>
                                <button
                                  onClick={(e: any) => {
                                    e.stopPropagation();
                                    togglePopup(loc?.location_id ?? '');
                                  }}
                                  className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                >
                                  <Trash size={20} />
                                  <p>Delete</p>
                                </button>
                              </div>
                            </div>
                          )}
                        </button>
                      </div>
                      <div className="flex w-full flex-row items-center space-x-2 border-b-[1px] border-[#EAECF0] pb-[8px] align-middle">
                        <Phone size={16} className="text-primary" />
                        <h2 className="text-[14px] text-primary">
                          {loc?.business_phone_number}
                        </h2>
                      </div>
                      <div className="flex flex-row items-center space-x-2 align-middle">
                        <div className="flex flex-row space-x-1 rounded-[24px] border-[1px] border-bordergray bg-[#F4F5F6] px-[8px] py-[2px] align-middle opacity-[50%]">
                          <Star
                            weight="fill"
                            className="text-[#F7A826]"
                            size={16}
                          />{' '}
                          <p className="text-[12px] text-graysecondary">4.9</p>
                        </div>
                        <div className="flex flex-row space-x-1 rounded-[24px] border-[1px] border-bordergray bg-[#F4F5F6] px-[8px] py-[2px] align-middle opacity-[50%]">
                          <Eye className="text-primary" size={16} />{' '}
                          <p className="text-[12px] text-graysecondary">2337</p>
                        </div>
                        <div className="flex flex-row space-x-1 rounded-[24px] border-[1px] border-bordergray bg-[#F4F5F6] px-[8px] py-[2px] align-middle opacity-[50%]">
                          <Cursor className="text-primary" size={16} />{' '}
                          <p className="text-[12px] text-graysecondary">295</p>
                        </div>
                        <p className="text-[12px] font-bold text-accent">
                          Coming Soon!
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-[200px] min-h-[200px] items-center justify-center align-middle">
                  <h2 className="text-[14px] font-semibold leading-[24px] text-primary">
                    No Locations
                  </h2>
                </div>
              )
            ) : (
              <div className="flex h-[200px] min-h-[200px] items-center justify-center align-middle">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingLocations}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full items-center justify-center align-middle"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default BusinessLocations;
